import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "资讯", to: "/news" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to) || location.pathname.startsWith("/detail");
  };

  const submitSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur transition-shadow ${
        scrolled ? "border-b border-background-200" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-background-50">
            <i className="ri-movie-2-line text-xl" />
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
            秋霞<span className="text-accent-600">影院</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden items-center md:flex">
            <div className="relative flex items-center">
              <i className="ri-search-line pointer-events-none absolute left-3 text-base text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视、资讯"
                className="h-10 w-52 rounded-full border border-background-300 bg-background-50 pl-9 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
              />
            </div>
          </form>

          <Link
            to="/search"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg" />
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-800 transition-colors hover:border-primary-300 md:hidden"
            aria-label="菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div ref={menuRef} className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="mx-auto w-full max-w-[1400px] space-y-1 px-4 py-3">
            <form onSubmit={submitSearch} className="relative mb-2 flex items-center">
              <i className="ri-search-line pointer-events-none absolute left-3 text-base text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视、资讯"
                className="h-11 w-full rounded-lg border border-background-300 bg-background-50 pl-9 pr-4 text-sm outline-none placeholder:text-foreground-400 focus:border-primary-400"
              />
            </form>
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/search"
              className="flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              <i className="ri-search-line" /> 站内搜索
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}